import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Key, Phone, Mail, Bell, CheckCircle, Wifi, PhoneForwarded, Plus, Trash2, ChevronUp, ChevronDown, Clock, Save, Users } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import { safeAsync, Badge, inp, btnPrimary, btnSm, cardWrap, neuShadow } from './crmShared';

export default function ApiSettingsTab({ apiSettings, crmNotifications, loadExtended }) {
  const apiCardsRef = useRef(null);
  const { onKeyDown: handleApiCardsKeyDown } = useArrowNav(apiCardsRef, '[data-api-card]', { horizontal: true, vertical: true });

  // Full per-provider config (non-secret fields, plus which secrets exist) so
  // the form can pre-fill saved values instead of showing blank every load.
  const [cfgs, setCfgs] = useState({});
  const loadConfigs = useCallback(async () => {
    const out = {};
    for (const p of ['twilio', 'sendgrid']) {
      try { const r = await window.zignagenixAPI.getCRMApiSetting(p); if (r) out[p] = r; } catch (_) { /* ignore */ }
    }
    setCfgs(out);
  }, []);
  useEffect(() => { loadConfigs(); }, [loadConfigs, apiSettings]);
  const reload = () => { loadExtended(); loadConfigs(); };

  const twCfg = (cfgs.twilio && cfgs.twilio.config) || {};
  const twSec = (cfgs.twilio && cfgs.twilio.secrets_present) || {};
  const sgCfg = (cfgs.sendgrid && cfgs.sendgrid.config) || {};
  const sgSec = (cfgs.sendgrid && cfgs.sendgrid.secrets_present) || {};
  const keepMsg = '•••••• saved — leave blank to keep';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Key size={18} color="#F26522" /> API Settings — Communication Providers
        </h3>
      </div>

      <div ref={apiCardsRef} onKeyDown={handleApiCardsKeyDown} style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* Twilio Card */}
        {(() => {
          const tw = apiSettings.find(s => s.provider === 'twilio');
          return (
            <div data-api-card tabIndex={0} style={{ ...cardWrap, padding: 24, flex: '1 1 300px', minWidth: 300, outline: 'none' }}
              onFocus={(e) => { e.currentTarget.style.boxShadow = 'none, 0 0 0 2px #f22f46'; }}
              onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: '#f22f4618', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Phone size={20} color="#f22f46" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>Twilio</div>
                  <div style={{ fontSize: 10, color: '#6C757D' }}>SMS, WhatsApp, Voice Calls</div>
                </div>
                <Badge text={tw?.is_active ? 'ACTIVE' : 'INACTIVE'} color={tw?.is_active ? '#62BB46' : '#6C757D'} />
              </div>
              <form key={`twilio-${cfgs.twilio ? 'loaded' : 'empty'}`} onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const accountSid = fd.get('account_sid');
                const authToken = fd.get('auth_token');
                if (!accountSid?.trim()) { alert('Twilio Account SID is required'); return; }
                // Auth token only required if none is already stored.
                if (!authToken?.trim() && !twSec.auth_token) { alert('Twilio Auth Token is required'); return; }
                await window.zignagenixAPI.saveCRMApiSetting({
                  provider: 'twilio', is_active: true,
                  config: { account_sid: accountSid, auth_token: authToken,
                    from_number: fd.get('from_number'), whatsapp_number: fd.get('whatsapp_number'),
                    voice_number: fd.get('voice_number'), webhook_base_url: fd.get('webhook_base_url'),
                    api_key_sid: fd.get('api_key_sid'), api_key_secret: fd.get('api_key_secret') }
                });
                alert('Twilio settings saved!');
                reload();
              }}>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Account SID</label>
                  <input name="account_sid" defaultValue={twCfg.account_sid || ''} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={inp} required />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Auth Token</label>
                  <input name="auth_token" type="password" placeholder={twSec.auth_token ? keepMsg : 'Your Twilio auth token'} style={inp} required={!twSec.auth_token} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>SMS From Number</label>
                  <input name="from_number" defaultValue={twCfg.from_number || ''} placeholder="+15551234567" style={inp} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>WhatsApp Number</label>
                  <input name="whatsapp_number" defaultValue={twCfg.whatsapp_number || ''} placeholder="+15551234567" style={inp} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Voice Number</label>
                  <input name="voice_number" defaultValue={twCfg.voice_number || ''} placeholder="+15551234567" style={inp} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Webhook Base URL <span style={{fontWeight:400,color:'#aaa'}}>(for delivery receipts)</span></label>
                  <input name="webhook_base_url" defaultValue={twCfg.webhook_base_url || ''} placeholder="https://portal.zignagenix.com" style={inp} />
                </div>
                <div style={{ borderTop: '1px solid #eee', paddingTop: 10, marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#F26522', display: 'block', marginBottom: 6 }}>Telemedicine Video (optional)</label>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>API Key SID</label>
                  <input name="api_key_sid" defaultValue={twCfg.api_key_sid || ''} placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={inp} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>API Key Secret</label>
                  <input name="api_key_secret" type="password" placeholder={twSec.api_key_secret ? keepMsg : 'Your API key secret'} style={inp} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" style={btnPrimary}><CheckCircle size={13} /> Save</button>
                  <button type="button" onClick={safeAsync(async () => {
                    const res = await window.zignagenixAPI.testCRMApiConnection('twilio');
                    alert(res.success ? `✓ ${res.message}` : `✗ ${res.message}`);
                    reload();
                  })} style={btnSm}><Wifi size={12} /> Test Connection</button>
                </div>
                {tw?.test_result && (
                  <div style={{ fontSize: 10, marginTop: 8, color: tw.test_result === 'pass' ? '#62BB46' : '#DC3545' }}>
                    Last test: {tw.test_result} {tw.last_tested_at ? `(${new Date(tw.last_tested_at).toLocaleString()})` : ''}
                  </div>
                )}
              </form>
            </div>
          );
        })()}

        {/* SendGrid Card */}
        {(() => {
          const sg = apiSettings.find(s => s.provider === 'sendgrid');
          return (
            <div data-api-card tabIndex={0} style={{ ...cardWrap, padding: 24, flex: '1 1 300px', minWidth: 300, outline: 'none' }}
              onFocus={(e) => { e.currentTarget.style.boxShadow = 'none, 0 0 0 2px #2E8B98'; }}
              onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: '#2E8B9818', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Mail size={20} color="#2E8B98" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>SendGrid</div>
                  <div style={{ fontSize: 10, color: '#6C757D' }}>Transactional Email via Twilio SendGrid</div>
                </div>
                <Badge text={sg?.is_active ? 'ACTIVE' : 'INACTIVE'} color={sg?.is_active ? '#62BB46' : '#6C757D'} />
              </div>
              <form key={`sendgrid-${cfgs.sendgrid ? 'loaded' : 'empty'}`} onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const apiKey = fd.get('api_key');
                if (!apiKey?.trim() && !sgSec.api_key) { alert('SendGrid API key is required'); return; }
                await window.zignagenixAPI.saveCRMApiSetting({
                  provider: 'sendgrid', is_active: true,
                  config: { api_key: apiKey, from_email: fd.get('from_email'), from_name: fd.get('from_name') || 'ZignaGenix CRM' }
                });
                alert('SendGrid settings saved!');
                reload();
              }}>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>API Key</label>
                  <input name="api_key" type="password" placeholder={sgSec.api_key ? keepMsg : 'SG.xxxxxxxxxxxxxxxxxxxxxxxx'} style={inp} required={!sgSec.api_key} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>From Email</label>
                  <input name="from_email" type="email" defaultValue={sgCfg.from_email || ''} placeholder="crm@yourdomain.com" style={inp} required />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>From Name</label>
                  <input name="from_name" defaultValue={sgCfg.from_name || ''} placeholder="ZignaGenix CRM" style={inp} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" style={btnPrimary}><CheckCircle size={13} /> Save</button>
                  <button type="button" onClick={safeAsync(async () => {
                    const res = await window.zignagenixAPI.testCRMApiConnection('sendgrid');
                    alert(res.success ? `✓ ${res.message}` : `✗ ${res.message}`);
                    reload();
                  })} style={btnSm}><Wifi size={12} /> Test Connection</button>
                </div>
                {sg?.test_result && (
                  <div style={{ fontSize: 10, marginTop: 8, color: sg.test_result === 'pass' ? '#62BB46' : '#DC3545' }}>
                    Last test: {sg.test_result} {sg.last_tested_at ? `(${new Date(sg.last_tested_at).toLocaleString()})` : ''}
                  </div>
                )}
              </form>
            </div>
          );
        })()}
      </div>

      {/* Existing SMTP note */}
      <div style={{ ...cardWrap, padding: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={14} color="#ADB5BD" />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Existing SMTP Config</span>
        </div>
        <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 4 }}>
          If you already have SMTP configured under Settings → Email, the CRM email channel will use that as a fallback when SendGrid is not configured. Both are supported simultaneously.
        </div>
      </div>

      {/* Inbound Auto Attendant — ring groups, hours, voicemail (Carlos, Phase 5g-b) */}
      <AutoAttendantPanel />

      {/* In-App Notification Settings */}
      <div style={{ ...cardWrap, padding: 24, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: '#256F7A18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bell size={20} color="#256F7A" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>In-App Notifications</div>
            <div style={{ fontSize: 10, color: '#6C757D' }}>No external API needed — delivered directly within ZignaGenix</div>
          </div>
          <Badge text="BUILT-IN" color="#256F7A" />
        </div>
        <div style={{ fontSize: 11, color: '#6C757D' }}>
          In-app notifications are sent to all active CRM staff members. {crmNotifications.length} notifications in queue.
          {crmNotifications.filter(n => !n.is_read).length > 0 && (
            <span style={{ color: '#F26522', fontWeight: 600 }}> ({crmNotifications.filter(n => !n.is_read).length} unread)</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Inbound Auto Attendant — ring groups (members, sequential/round-robin),
// business hours, and voicemail notification recipients. Drives the Twilio
// voice webhook (routes/twilio_webhooks.js + _patient_calls.js).
const DAYS = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['0', 'Sun']];

function AutoAttendantPanel() {
  const [groups, setGroups] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});   // groupId -> { label, phone }
  const [vmEmail, setVmEmail] = useState('');
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const g = await window.zignagenixAPI.invoke('crm:ringgroup:list'); setGroups(Array.isArray(g) ? g : []);
      const c = await window.zignagenixAPI.invoke('crm:ivr:config:get'); setCfg(c && !c.error ? c : {});
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  const inv = (ch, a) => window.zignagenixAPI.invoke(ch, a);

  async function addMember(groupId) {
    const d = draft[groupId] || {};
    if (!(d.phone || '').trim()) { setErr('Enter a phone number'); return; }
    const r = await inv('crm:ringgroup:member:add', { group_id: groupId, label: d.label, phone: d.phone });
    if (r && r.error) setErr(r.message || r.code);
    else { setDraft((s) => ({ ...s, [groupId]: { label: '', phone: '' } })); setErr(null); load(); }
  }
  async function rmMember(id) { if (!window.confirm('Remove this person from the group?')) return; await inv('crm:ringgroup:member:remove', { id }); load(); }
  async function tgMember(m) { await inv('crm:ringgroup:member:toggle', { id: m.id, active: !m.active }); load(); }
  async function mvMember(group, idx, dir) {
    const arr = [...group.members]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await inv('crm:ringgroup:member:reorder', { orderedIds: arr.map((m) => m.id) }); load();
  }
  async function updateGroup(g, patch) { await inv('crm:ringgroup:update', { id: g.id, strategy: g.strategy, ring_seconds: g.ring_seconds, active: g.active, ...patch }); load(); }

  const hours = (cfg && cfg.hours) || {};
  const vm = (cfg && cfg.voicemail) || {};
  const menu = (cfg && cfg.menu) || {};

  function setHour(dow, idx, val) { setCfg((c) => { const h = { ...(c.hours || {}) }; const win = Array.isArray(h[dow]) ? [...h[dow]] : ['08:00', '17:00']; win[idx] = val; h[dow] = win; return { ...c, hours: h }; }); }
  function setClosed(dow, closed) { setCfg((c) => { const h = { ...(c.hours || {}) }; h[dow] = closed ? null : ['08:00', '17:00']; return { ...c, hours: h }; }); }
  async function saveHours() { const r = await inv('crm:ivr:config:save', { hours: cfg.hours || {} }); if (r && r.error) setErr(r.message); else flash('Hours saved'); }

  async function addVmEmail() { const e = vmEmail.trim(); if (!e || !e.includes('@')) { setErr('Enter a valid email'); return; } const list = Array.isArray(vm.recipients) ? vm.recipients : []; if (list.includes(e)) { setVmEmail(''); return; } const next = { ...vm, recipients: [...list, e] }; const r = await inv('crm:ivr:config:save', { voicemail: next }); if (r && r.error) setErr(r.message); else { setVmEmail(''); flash('Recipient added'); load(); } }
  async function rmVmEmail(e) { const next = { ...vm, recipients: (vm.recipients || []).filter((x) => x !== e) }; const r = await inv('crm:ivr:config:save', { voicemail: next }); if (r && r.error) setErr(r.message); else { flash('Recipient removed'); load(); } }

  return (
    <div style={{ ...cardWrap, padding: 24, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: '#62BB4618', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PhoneForwarded size={20} color="#62BB46" />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>Inbound Auto Attendant</div>
          <div style={{ fontSize: 10, color: '#6C757D' }}>Ring groups, business hours, and voicemail for the toll-free number.</div>
        </div>
      </div>
      {err && <div style={{ fontSize: 11, color: '#8b0000', margin: '6px 0' }}>{err}</div>}
      {msg && <div style={{ fontSize: 11, color: '#1a7f5a', margin: '6px 0' }}>{msg}</div>}

      {/* Ring groups + members */}
      {groups.map((g) => (
        <div key={g.id} style={{ border: '1px solid #e1e6ed', borderRadius: 8, padding: 12, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <Users size={15} color="#2E8B98" />
            <strong style={{ fontSize: 13 }}>{g.name}</strong>
            <span style={{ flex: 1 }} />
            <label style={{ fontSize: 11, color: '#6C757D' }}>Ring:&nbsp;
              <select value={g.strategy} onChange={(e) => updateGroup(g, { strategy: e.target.value })} style={{ ...inp, width: 'auto', padding: '3px 6px', fontSize: 11 }}>
                <option value="round_robin">Round-robin</option>
                <option value="sequential">In order</option>
              </select>
            </label>
            <label style={{ fontSize: 11, color: '#6C757D' }}>&nbsp;Sec:&nbsp;
              <input type="number" min="5" max="60" defaultValue={g.ring_seconds} onBlur={(e) => updateGroup(g, { ring_seconds: parseInt(e.target.value, 10) })} style={{ ...inp, width: 56, padding: '3px 6px', fontSize: 11 }} />
            </label>
          </div>
          {(g.members || []).length === 0 ? (
            <div style={{ fontSize: 11.5, color: '#9aa4b2', fontStyle: 'italic' }}>No one in this group yet.</div>
          ) : g.members.map((m, idx) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f4f6f9', opacity: m.active ? 1 : 0.5 }}>
              <span style={{ width: 20, textAlign: 'center', fontSize: 11, color: '#9aa4b2' }}>{idx + 1}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{m.label || m.phone}</span>
                {m.label && <span style={{ fontSize: 11, color: '#6C757D' }}> · {m.phone}</span>}
              </div>
              <button type="button" onClick={() => mvMember(g, idx, -1)} disabled={idx === 0} title="Up" style={iconBtn}><ChevronUp size={13} /></button>
              <button type="button" onClick={() => mvMember(g, idx, 1)} disabled={idx === g.members.length - 1} title="Down" style={iconBtn}><ChevronDown size={13} /></button>
              <button type="button" onClick={() => tgMember(m)} style={{ ...pill, background: m.active ? '#e6f7ee' : '#f1f3f5', color: m.active ? '#1a7f5a' : '#6C757D', borderColor: m.active ? '#b6e3cd' : '#dde1e6' }}>{m.active ? 'On' : 'Off'}</button>
              <button type="button" onClick={() => rmMember(m.id)} title="Remove" style={{ ...iconBtn, color: '#b00' }}><Trash2 size={13} /></button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input value={(draft[g.id] || {}).label || ''} onChange={(e) => setDraft((s) => ({ ...s, [g.id]: { ...(s[g.id] || {}), label: e.target.value } }))} placeholder="Name" style={{ ...inp, flex: 1, fontSize: 12 }} />
            <input value={(draft[g.id] || {}).phone || ''} onChange={(e) => setDraft((s) => ({ ...s, [g.id]: { ...(s[g.id] || {}), phone: e.target.value } }))} placeholder="+1 (619) 000-0000" style={{ ...inp, flex: 1, fontSize: 12 }} />
            <button type="button" onClick={() => addMember(g.id)} style={btnSm}><Plus size={12} /> Add</button>
          </div>
        </div>
      ))}

      {/* Business hours */}
      <div style={{ border: '1px solid #e1e6ed', borderRadius: 8, padding: 12, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><Clock size={15} color="#2E8B98" /><strong style={{ fontSize: 13 }}>Business hours</strong><span style={{ fontSize: 10.5, color: '#9aa4b2' }}>({(cfg && cfg.timezone) || 'America/Los_Angeles'})</span></div>
        {DAYS.map(([dow, name]) => {
          const win = hours[dow]; const closed = !Array.isArray(win);
          return (
            <div key={dow} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
              <span style={{ width: 40, fontSize: 12, fontWeight: 600 }}>{name}</span>
              <label style={{ fontSize: 11, color: '#6C757D' }}><input type="checkbox" checked={closed} onChange={(e) => setClosed(dow, e.target.checked)} /> Closed</label>
              {!closed && (<>
                <input type="time" value={win[0]} onChange={(e) => setHour(dow, 0, e.target.value)} style={{ ...inp, width: 110, padding: '3px 6px', fontSize: 12 }} />
                <span style={{ color: '#9aa4b2' }}>–</span>
                <input type="time" value={win[1]} onChange={(e) => setHour(dow, 1, e.target.value)} style={{ ...inp, width: 110, padding: '3px 6px', fontSize: 12 }} />
              </>)}
            </div>
          );
        })}
        <button type="button" onClick={saveHours} style={{ ...btnSm, marginTop: 8 }}><Save size={12} /> Save hours</button>
      </div>

      {/* Voicemail recipients */}
      <div style={{ border: '1px solid #e1e6ed', borderRadius: 8, padding: 12, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><Mail size={15} color="#2E8B98" /><strong style={{ fontSize: 13 }}>Voicemail notification recipients</strong></div>
        <div style={{ fontSize: 10.5, color: '#9aa4b2', marginBottom: 8 }}>Emailed (with transcription) when a new voicemail arrives.</div>
        {(vm.recipients || []).map((e) => (
          <div key={e} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12.5 }}>
            <span style={{ flex: 1 }}>{e}</span>
            <button type="button" onClick={() => rmVmEmail(e)} title="Remove" style={{ ...iconBtn, color: '#b00' }}><Trash2 size={13} /></button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input value={vmEmail} onChange={(e) => setVmEmail(e.target.value)} placeholder="name@zignagenix.com" style={{ ...inp, flex: 1, fontSize: 12 }} />
          <button type="button" onClick={addVmEmail} style={btnSm}><Plus size={12} /> Add</button>
        </div>
      </div>

      {/* Menu (read-only) */}
      <div style={{ border: '1px solid #e1e6ed', borderRadius: 8, padding: 12, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><Phone size={15} color="#2E8B98" /><strong style={{ fontSize: 13 }}>Menu</strong></div>
        {Object.keys(menu).sort().map((d) => (
          <div key={d} style={{ fontSize: 12, color: '#3a4a5e', padding: '2px 0' }}>
            <b>Press {d}</b> — {menu[d].label} <span style={{ color: '#9aa4b2' }}>→ {menu[d].group_key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, background: '#fff', border: '1px solid #c9d6e8', borderRadius: 5, cursor: 'pointer', color: '#3a4a5e' };
const pill = { fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10, border: '1px solid', cursor: 'pointer' };
